import { AfterContentInit, Component, ElementRef, OnChanges, SimpleChanges, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { filter } from 'rxjs';

import { StepsNavigationComponent } from '../../../../Common/Layout/components/StepsNavigation/steps-navigation.component';
import { StepsBannerErrorConfigModel } from '../../../../Common/Misc/models/banner-error-config.model';
import { BriefingsService } from '@Monitor/Briefings/services/briefings.service';
import { MonitoringRoutingService } from '@Monitor/Core/services/monitoring-routing.service';
import { BriefingModel } from '@Monitor/Briefings/models/briefing.model';
import { MonitoringRouteModel } from '@Monitor/Shared/models/monitoring-route.model';
import { StepStatesEnum } from '../../../../Common/Layout/components/StepsNavigation/step-states.enum';

export enum BriefingStepsEnum {
    compose,
    publishingSettings,
    reviewSchedule
}

export enum BriefingStepIds {
    compose = 'compose_briefing',
    publishingSettings = 'publishing_settings',
    reviewSchedule = 'review_schedule',
}

@Component({
    selector: 'agpr-briefing-steps-navigation',
    templateUrl: '../../../../Common/Layout/components/StepsNavigation/steps-navigation.component.html',
    styleUrls: ['../../../../Common/Layout/components/StepsNavigation/steps-navigation.component.scss'],
    standalone: false
})
export class BriefingsStepsNavigationComponent extends StepsNavigationComponent implements OnChanges, AfterContentInit, OnDestroy {

    briefing: BriefingModel;
    currentRoute: MonitoringRouteModel;

    readonly tabIdToIndex = new Map<string, number>([
        [BriefingStepIds.compose, BriefingStepsEnum.compose],
        [BriefingStepIds.publishingSettings, BriefingStepsEnum.publishingSettings],
        [BriefingStepIds.reviewSchedule, BriefingStepsEnum.reviewSchedule]
    ]);

    private tabIndexToString: Map<BriefingStepsEnum, string> = new Map([
        [BriefingStepsEnum.compose, 'compose'],
        [BriefingStepsEnum.publishingSettings, 'publishingSettings'],
        [BriefingStepsEnum.reviewSchedule, 'reviewSchedule']
    ]);

    constructor(
        el: ElementRef<HTMLElement>,
        public briefingsService: BriefingsService,
        public monitoringRoutingService: MonitoringRoutingService,
        private router: Router,
        private activatedRoute: ActivatedRoute
    ) {
        super(el);
    }

    ngAfterContentInit() {
        super.ngAfterContentInit();
        requestAnimationFrame(() => {
            this.subscriptions.push(
                this.briefingsService.getFocusedResource().pipe(filter(x => !!x)).subscribe(briefing => {
                    const briefChanged = this.briefing && (briefing.id !== this.briefing.id);
                    this.briefing = briefing;

                    if (briefChanged) {
                        this.allErrors = [];
                        this.clearStepStates(BriefingStepIds.compose);
                        this.setClasses();
                    }
                }),
                this.monitoringRoutingService.getCurrentRoute().subscribe(
                    route => {
                        this.currentRoute = route;
                        requestAnimationFrame(() => {
                            if (this.currentRoute.queryParamMap?.get('step')) {
                                this.selectTabByIndex(
                                    Array.from(this.tabIndexToString.values()).findIndex(x => x === this.currentRoute.queryParamMap.get('step')),
                                    true,
                                    false
                                );
                            } else {
                                this.selectTabByIndex(BriefingStepsEnum.compose, true, false);
                            }
                        });
                    }
                )
            );
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('selectedTabIndex')) {
            if (this.briefing) {
                const stepValue = this.tabIndexToString.get(changes['selectedTabIndex'].currentValue);
                this.router.navigate(
                    [],
                    {
                        relativeTo: this.activatedRoute,
                        queryParams: { step: stepValue },
                        queryParamsHandling: 'merge'
                    }
                );
            }
            this.handleStepStates(this.allErrors);
            this.setClasses();
        }
    }

    protected handleStepStates(errors: StepsBannerErrorConfigModel[]) {
        if (!this.tabs || this.tabs.length === 0) {
            return;
        }

        // touched state is defined by a tab that is behind or equal to the current selected tab
        // all touch states must be calculated before tab state calculations
        this.tabs?.toArray().forEach(tab => {
            if (this.tabIdToIndex.get(tab.id) <= this._selectedTabIndex) {
                tab.touched = true;
            }
        });


        this.tabs?.toArray().forEach(tab => {
            let hasErrors: boolean = false;
            switch(tab.id) {
                case BriefingStepIds.compose:
                    hasErrors = !!errors.find(x => x.tab === BriefingStepsEnum.compose);
                    break;
                case BriefingStepIds.publishingSettings:
                    hasErrors = !!errors.find(x => x.tab === BriefingStepsEnum.publishingSettings);
                    break;
            }

            if (tab.selected) {
                tab.iconState = StepStatesEnum.selected;
            } else if (tab.touched === false) {
                tab.iconState = StepStatesEnum.open;
            } else if (tab.touched === true) {
                if (hasErrors) {
                    tab.error = true;
                    const reviewTab = this.tabs.toArray().find(x => x.id === BriefingStepIds.reviewSchedule);
                    if (reviewTab?.touched) {
                        // only show error state on tabs when review tab has been touched
                        tab.iconState = StepStatesEnum.error;
                    } else {
                        tab.iconState = StepStatesEnum.inProgress;
                    }
                } else {
                    // special case, since review step doesn't have an error state, calculate state based on if other steps have errors
                    if (tab.id === BriefingStepIds.reviewSchedule) {
                        tab.iconState = (this.tabs.toArray().some(x => x.error)) ? StepStatesEnum.inProgress
                            : StepStatesEnum.completed;
                    } else {
                        tab.error = false;
                        tab.iconState = StepStatesEnum.completed;
                    }
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
    }
}
